<template>
  <div>
    <h3 class="product-card-title" v-if="name">
      {{ name }} <span v-if="badge" class="badge">{{ badge }}</span>
    </h3>
    <div class="product-download-card">
      <ProductDownloadButton :handleDownload="handleDownload" :release="release" :disabled="isDownloading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/UserStore'
import { useAmplitude, BrazeEvents } from '@outputinc/o-analytics'
import { useBrazeStore } from '@/store/BrazeStore'
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { usePlanStore } from '@/store/PlanStore'
import { ClientRelease } from '@/types'
import ProductDownloadButton from '@/components/shared/screens/ProductDownloadButton.vue'

const brazeStore = useBrazeStore()
const userStore = useUserStore()
const planStore = usePlanStore()
const { track, Events } = useAmplitude()
const { trackEmailUser } = brazeStore
const { outputUid } = storeToRefs(userStore)
const { selectedPlanProduct } = storeToRefs(planStore)

const props = defineProps<{
  id: string
  name?: string
  badge?: string
  release: ClientRelease
}>()

const isDownloading = ref(false)

const handleDownload = async (platform: string, clientRelease: ClientRelease) => {
  // Guard against a double-click firing the event/download twice during the await below.
  // The installer URL downloads via Content-Disposition rather than navigating the page,
  // so reset the flag in finally — otherwise the button stays disabled forever (WEB-7818).
  if (isDownloading.value) return
  isDownloading.value = true

  const release = platform === 'win' ? clientRelease?.win.installer : clientRelease?.mac.installer
  track(Events.WEBSITE_SUBSCRIPTION_APP_DOWNLOAD, {
    os: platform,
    products: [selectedPlanProduct.value],
    downloadId: props.id
  })

  // Await the Braze mutation before navigating — window.open('_self') tears down the
  // page and Safari/Firefox abort the in-flight POST if we fire-and-forget (WEB-7818).
  // Errors are already reported via BrazeStore.onTrackEmailError; catch so a rejection
  // (network abort or GraphQL error) doesn't surface as an unhandled rejection, and
  // the download still runs.
  try {
    await trackEmailUser({
      externalId: outputUid.value,
      name: BrazeEvents.WEBSITE_APP_DOWNLOAD,
      time: new Date().toISOString(),
      properties: {
        os: platform,
        products: selectedPlanProduct.value,
        downloadId: props.id
      }
    })
  } catch {
    // reported via BrazeStore.onTrackEmailError
  } finally {
    window.open(release, '_self')
    isDownloading.value = false
  }
}
</script>

<style lang="scss" scoped>
.product-card-title {
  font-family: $font-family-pilat-bold;
  font-size: 32px;
  font-weight: 700;
  margin: $spacer-2 0;
  max-width: 330px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: $spacer-2;
  .badge {
    background-color: transparent;
    border: 1px solid var(--color-text-primary);
    color: var(--color-text-primary);
    border-radius: 24px;
    padding: 2px $spacer-2;
    font-size: $fs_2;
    font-weight: 500;
    line-height: 16px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }
}

.product-download-card {
  width: 330px;
  height: 176px;
  display: flex;
  padding: $spacer-5 $spacer-4;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: $spacer-3;
  border-radius: 12px;
  border: 2px solid var(--color-border-card);
}
</style>
